import { useMemo } from 'react';

import Component from './component';
import {
  ACTIVITY_BUILDER_DRAWER_EMPTY_EDIT,
  ACTIVITY_BUILDER_GLOBAL_ADD_TOP,
  SectionOrSubSection,
} from '../activity-builder.constants';
import {
  getMoveComponentAriaLabel,
  getStyleProperties,
} from './components.utils';
import { useComponentMutations } from 'approot/activity-builder/activity-builder.hooks';
import { ComponentAddMenuTarget } from './components-menu/components-add-menu';
import { SettingsComponentMenu } from '../settings/settings-component-menu';
import { Droppable } from '../drag-drop/droppable';
import { useActivityBuilderContext } from '../activity-builder.context';
import { getActivityNumber } from './activity/activity.utils';
import { Draggable } from '../drag-drop/draggable';
import {
  sortComponents,
  getNewSortValue,
  generateKeyBasedOnSort,
  getFirstSortValue,
} from '../activity-builder.utils';
import ActivityBuilderSlide from '../activity-builder-slide';
import {
  ActivityBuilderRow,
  ActivityBuilderSlideControlsRow,
} from '../activity-builder-slide.style';
import type { LessonActivitiesFragment } from '../lesson-builder.graphql.generated';
import {
  ACTIVITIES_COMPONENT_TYPE_ACTIVITY,
  ACTIVITIES_COMPONENT_TYPE_IMAGE,
  LESSON_ACTIVITIES,
  ActivitiesContentType,
} from 'global/constants';
import ActivityEdit from './activity/activity-edit';
import {
  ComponentConfirmDelete,
  useComponentConfirmDelete,
} from './shared/component-confirm/component-confirm';
import { hasImage } from './image/image.utils';
import PageType from '../page/activity-builder-page-type';
import IconTrash from 'approot/shared/icons/icon-trash';
import IconArrowUp from 'approot/shared/icons/icon-arrow-up';
import IconArrowDown from 'approot/shared/icons/icon-arrow-down';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  section: SectionOrSubSection;
  contentId: number;
  contentType: ActivitiesContentType;
  lessonId: number;
  sectionsOverview: Pluck<LessonActivitiesFragment, 'sectionsOverview'>[];
};

const Components = ({
  section,
  contentId,
  contentType,
  lessonId,
  sectionsOverview,
}: Props) => {
  const components = section?.components;

  const visibleComponents = useMemo(() => {
    const sortedComponents = sortComponents(components);
    return sortedComponents.filter(
      c => c.type !== ACTIVITIES_COMPONENT_TYPE_ACTIVITY
    );
  }, [components]);

  const {
    activeComponent,
    setComponentDrawerId,
    setActiveComponent,
    isEditDrawerActive,
    isAddDrawerActive,
    closeDrawer,
  } = useActivityBuilderContext();

  const { handleAdd, handleMoveDown, handleMoveUp, handleDelete } =
    useComponentMutations({
      sectionId: section.id,
      components: visibleComponents,
    });

  const { deleteComponentId, componentToDelete, setDeleteComponentId } =
    useComponentConfirmDelete({ components: visibleComponents });

  return (
    <ActivityBuilderRow>
      <ActivityBuilderSlideControlsRow>
        <ActivityEdit
          section={section}
          sectionsOverview={sectionsOverview}
          contentId={contentId}
          contentType={contentType}
        />
        {contentType === LESSON_ACTIVITIES && (
          <PageType
            id={section.id}
            contentId={contentId}
            sort={section.sort}
            pageType={section.pageType}
            contentType={contentType}
          />
        )}
      </ActivityBuilderSlideControlsRow>

      {deleteComponentId && componentToDelete && (
        <ComponentConfirmDelete
          component={componentToDelete}
          handleClose={() => setDeleteComponentId(null)}
          handleConfirm={() => {
            handleDelete(componentToDelete);
            setDeleteComponentId(null);

            // if the edit drawer is active set to empty
            if (isEditDrawerActive()) {
              setActiveComponent(undefined);
              setComponentDrawerId(ACTIVITY_BUILDER_DRAWER_EMPTY_EDIT);
            } else if (isAddDrawerActive()) {
              // close the add drawer as picking a new target is harder
              closeDrawer();
            }
          }}
        />
      )}

      <ActivityBuilderSlide>
        <Droppable
          key={visibleComponents?.[0]?.id}
          sectionId={section.id}
          nextComponentId={visibleComponents?.[0]?.id}
          sortedComponents={visibleComponents}
        />
        <ComponentAddMenuTarget
          componentId={ACTIVITY_BUILDER_GLOBAL_ADD_TOP}
          isLastComponent={false}
          isAlwaysVisible={visibleComponents.length === 0}
          addComponent={props => {
            handleAdd({
              ...props,
              sort:
                visibleComponents.length === 0
                  ? 'z'
                  : getFirstSortValue(visibleComponents),
            });
          }}
        />
        {visibleComponents.map((component, index) => {
          return (
            <div
              data-selector={`component-section-${component.type}`}
              key={generateKeyBasedOnSort({
                id: component.id,
                components: visibleComponents,
              })}
            >
              <Component
                component={component}
                contentId={contentId}
                contentType={contentType}
                lessonId={lessonId}
                render={({ component: comp, settings, customSettingsMenu }) => {
                  const number = getActivityNumber(
                    section.id,
                    sectionsOverview
                  );

                  return (
                    <Draggable
                      id={component.id}
                      type={component.type}
                      typename={component.__typename}
                      index={index}
                      sortedComponents={visibleComponents}
                      sectionId={section.id}
                      onClick={(e, el, componentId) => {
                        // no related target means the click did not happen on a component inside a column
                        if (!e.relatedTarget && el.contains(e.target as Node)) {
                          if (activeComponent?.id !== componentId) {
                            setActiveComponent({ id: componentId });
                          }

                          if (isEditDrawerActive()) {
                            setComponentDrawerId(componentId);
                          }
                        }
                      }}
                    >
                      {activeComponent?.id === component.id && (
                        <SettingsComponentMenu
                          componentId={component.id}
                          sectionId={section.id}
                          style={getStyleProperties(component)}
                          componentSettings={settings}
                          columnSettings={settings}
                          type={component.type}
                          customSettingsMenu={customSettingsMenu}
                          hasImage={
                            !!(
                              component.type ===
                                ACTIVITIES_COMPONENT_TYPE_IMAGE &&
                              hasImage(component)
                            )
                          }
                          addComponent={props => {
                            handleAdd({
                              ...props,
                              sort: getNewSortValue({
                                components: visibleComponents,
                                componentId: component.id,
                              }),
                            });
                          }}
                          options={[
                            ...(index > 0
                              ? [
                                  {
                                    onClick: () =>
                                      handleMoveUp(component, index),
                                    ariaLabel: getMoveComponentAriaLabel(
                                      component.type,
                                      'up',
                                      number
                                    ),
                                    icon: (
                                      <IconArrowUp width={20} height={20} />
                                    ),
                                    divider:
                                      index + 1 === visibleComponents.length,
                                    tooltip: 'Move up',
                                  },
                                ]
                              : []),
                            ...(index + 1 < visibleComponents.length
                              ? [
                                  {
                                    onClick: () =>
                                      handleMoveDown(component, index),
                                    ariaLabel: getMoveComponentAriaLabel(
                                      component.type,
                                      'down',
                                      number
                                    ),
                                    icon: (
                                      <IconArrowDown width={20} height={20} />
                                    ),
                                    divider: true,
                                    tooltip: 'Move down',
                                  },
                                ]
                              : []),
                            {
                              onClick: () => setDeleteComponentId(component.id),
                              ariaLabel: `Remove this ${component.type} component`,
                              icon: <IconTrash width={20} height={20} />,
                              tooltip: 'Delete',
                            },
                          ]}
                        />
                      )}
                      {comp}
                    </Draggable>
                  );
                }}
              />
              <Droppable
                sectionId={section.id}
                previousComponentId={component.id}
                nextComponentId={visibleComponents[index + 1]?.id}
                sortedComponents={visibleComponents}
              />
              <ComponentAddMenuTarget
                componentId={component.id}
                isLastComponent={index === visibleComponents.length - 1}
                addComponent={props => {
                  handleAdd({
                    ...props,
                    sort: getNewSortValue({
                      components: visibleComponents,
                      componentId: component.id,
                    }),
                  });
                }}
              />
            </div>
          );
        })}
      </ActivityBuilderSlide>
    </ActivityBuilderRow>
  );
};

export default Components;
